import React, { useContext, useEffect, useRef, useState } from "react";
import { LanguageContext } from "../Contexts/LanguageContext";
import { motion, AnimatePresence } from "framer-motion";

const LANGS = [
  { code: "en", label: "English" },
  { code: "hi", label: "Hindi" },
  { code: "bho", label: "Bhojpuri" },
  { code: "pa", label: "Punjabi" },
];

function LanguageSelector({ darkMode }) {
  const { state, dispatch } = useContext(LanguageContext);
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  const current = LANGS.find((l) => l.code === state.language) || LANGS[0];

  useEffect(() => {
    const onDoc = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("touchstart", onDoc, { passive: true });
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("touchstart", onDoc);
    };
  }, []);

  const select = (code) => {
    dispatch({ type: "SET_LANGUAGE", payload: code });
    setOpen(false);
  };

  const triggerBase =
    "inline-flex w-full min-w-[6.25rem] md:min-w-[10.5rem] items-center justify-between gap-2 rounded-xl border px-3 py-2 text-sm font-medium shadow-sm transition focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2";
  const triggerLight =
    "border-slate-200/80 bg-white/90 text-slate-800 hover:bg-slate-50 hover:border-slate-300";
  const triggerDark =
    "border-slate-600/80 bg-slate-800/90 text-slate-100 hover:bg-slate-700 hover:border-slate-500";

  const panelLight =
    "border border-slate-200/90 bg-white/95 text-slate-800 shadow-xl backdrop-blur-md";
  const panelDark =
    "border border-slate-600/80 bg-slate-900/95 text-slate-100 shadow-xl backdrop-blur-md";

  const itemIdleLight = "hover:bg-blue-50 hover:text-blue-800";
  const itemIdleDark = "hover:bg-slate-800 hover:text-white";
  const itemActive = "bg-blue-600 text-white font-semibold";

  return (
    <div
      ref={containerRef}
      className="relative z-[60] mx-1 inline-block min-w-[6.25rem] align-middle md:mx-3 md:min-w-[10.5rem]"
    >
      <button
        type="button"
        className={`${triggerBase} ${darkMode ? triggerLight : triggerDark}`}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Select language"
      >
        <span className="min-w-0 shrink truncate">{current.label}</span>
        <svg
          className={`h-4 w-4 shrink-0 opacity-70 transition-transform ${open ? "rotate-180" : ""}`}
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden
        >
          <path
            fillRule="evenodd"
            d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.94a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
            clipRule="evenodd"
          />
        </svg>
      </button>

      <AnimatePresence>
        {open && (
          <motion.ul
            role="listbox"
            aria-label="Languages"
            initial={{ opacity: 0, y: -6, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.98 }}
            transition={{ duration: 0.15 }}
            className={`absolute left-0 top-full z-[70] mt-1.5 w-full overflow-hidden rounded-xl py-1 ${darkMode ? panelLight : panelDark}`}
          >
            {LANGS.map((lang) => {
              const active = lang.code === state.language;
              return (
                <li key={lang.code} role="option" aria-selected={active}>
                  <button
                    type="button"
                    className={`flex w-full min-w-0 items-center justify-between gap-1 px-3 py-2.5 text-left text-sm transition ${
                      active
                        ? itemActive
                        : darkMode
                          ? `${itemIdleLight} text-slate-800`
                          : `${itemIdleDark} text-slate-200`
                    }`}
                    onClick={() => select(lang.code)}
                    title={lang.label}
                  >
                    <span className="min-w-0 truncate">{lang.label}</span>
                    {active && (
                      <svg
                        className="h-4 w-4 shrink-0 text-white"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        aria-hidden
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z"
                          clipRule="evenodd"
                        />
                      </svg>
                    )}
                  </button>
                </li>
              );
            })}
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  );
}

export default LanguageSelector;
